import { useMutation } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { GenericErrorStatus } from '../../../types/errors/ErrorStatus';
import useLog from '../utils/log/useLog';
import useUrlInfo from '../utils/urls/useUrlInfo';

export interface UseCreateMutationArgs<TData> {
  query: DocumentNode;
  defaultResponse: TData;
}

export const useCreateMutation = <TData, TVariables>({ query, defaultResponse }: UseCreateMutationArgs<TData>) => {
  const [mutate, states] = useMutation<TData, TVariables>(query);
  const { pid, requestId } = useUrlInfo().states;
  const { logError } = useLog().actions;

  const create = async (request: TVariables) => {
    try {
      const response = await mutate({ variables: request });
      if (!response.data) {
        logError(GenericErrorStatus.APOLLO_ERROR, {
          pid,
          requestId,
          query: 'useCreateMutation',
          error: `Unsuccessful create mutation: ${response.errors?.map((e) => e.message).join('') ?? ''}`,
        });
        return defaultResponse;
      }
      return response.data;
    } catch (error) {
      logError(GenericErrorStatus.APOLLO_ERROR, {
        pid,
        requestId,
        query: 'useCreateMutation',
        error: `Error create mutation: ${error}`,
      });
      return defaultResponse;
    }
  };

  return {
    states,
    actions: {
      create,
    },
  };
};

export default useCreateMutation;
